import Modal from 'react-bootstrap/Modal';
import { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import API_ENDPOINTS from '../../config/apiConfig';
import { fetchFromApi } from '../../config/api';

function AddProductModal(props) {
    const { artistData, categoriesData, fetchProductsData } = useData();
    const [productData, setProductData] = useState({
        product_name: '', product_image: '', product_cost: '',
        image_dimensions: '', product_description: '', product_short_description: '',
        product_discount_percentage: 0, product_final_price: '',
        product_artist: 0, product_category_id: 0,
        product_available: 1,
    });
    const [thumbnailImages, setThumbnailImages] = useState([]);
    const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    useEffect(() => {
        const { product_cost, product_discount_percentage } = productData;
        if (product_cost && product_discount_percentage) {
            const final = product_cost - (product_cost * product_discount_percentage) / 100;
            setProductData((p) => ({ ...p, product_final_price: final.toFixed(0) }));
        } else {
            setProductData((p) => ({ ...p, product_final_price: '' }));
        }
    }, [productData.product_cost, productData.product_discount_percentage]);

    const handleChange = (e) => setProductData({ ...productData, [e.target.name]: e.target.value });

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                const res = await fetch(API_ENDPOINTS.UPLOAD_IMAGE, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ image: reader.result, type: "products" })
                });
                const data = await res.json();
                if (data.status === "success") setProductData((p) => ({ ...p, product_image: data.url }));
                else alert("Image upload failed.");
            } catch { alert("Image upload error."); }
        };
        reader.readAsDataURL(file);
    };

    const handleThumbnailChange = async (e) => {
        setIsLoadingThumbnails(true);
        const files = Array.from(e.target.files);
        try {
            const urls = await Promise.all(files.map((file) => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    try {
                        const res = await fetch(API_ENDPOINTS.UPLOAD_IMAGE, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ image: reader.result, type: "products" })
                        });
                        const data = await res.json();
                        data.status === "success" ? resolve(data.url) : reject("Upload failed");
                    } catch (err) { reject(err); }
                };
                reader.readAsDataURL(file);
            })));
            const existing = thumbnailImages || [];
            setThumbnailImages([...existing, ...urls.map((image, i) => ({ order: existing.length + i + 1, image }))]);
        } catch { console.error("Thumbnail upload error"); }
        finally { setIsLoadingThumbnails(false); }
    };

    const removeThumbnail = (idx) =>
        setThumbnailImages(thumbnailImages.filter((_, i) => i !== idx).map((t, i) => ({ ...t, order: i + 1 })));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!productData.product_name || !productData.product_cost || !productData.product_image) {
            setError("Please fill all required fields."); return;
        }
        setLoading(true); setError("");
        try {
            const data = await fetchFromApi(API_ENDPOINTS.INSERT_PRODUCT, 'POST', {
                ...productData, thumblist: JSON.stringify(thumbnailImages)
            });
            if (data.status === "success") {
                setSuccessMsg("Product added successfully!");
                setTimeout(async () => { setSuccessMsg(""); props.onHide(); await fetchProductsData(); }, 1500);
            } else { setError("Failed to add product. Please try again."); }
        } catch { setError("An error occurred. Please try again."); }
        finally { setTimeout(() => setLoading(false), 1500); }
    };

    const isFormValid = productData.product_name && productData.product_artist > 0 &&
        productData.product_category_id > 0 && productData.product_cost && productData.product_cost <= 9999;

    return (
        <Modal {...props} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="modal-form-grid">
                        <div className="form-field">
                            <label>Product Name <span className="req">*</span></label>
                            <input type="text" name="product_name" value={productData.product_name} onChange={handleChange} placeholder="Enter product name" />
                        </div>
                        <div className="form-field">
                            <label>Category <span className="req">*</span></label>
                            <select name="product_category_id" value={productData.product_category_id} onChange={handleChange}>
                                <option value="0">Select category</option>
                                {categoriesData.map((c) => <option key={c.id} value={c.id}>{c.category_name}</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label>Artist <span className="req">*</span></label>
                            <select name="product_artist" value={productData.product_artist} onChange={handleChange}>
                                <option value="0">Select artist</option>
                                {artistData.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label>Image Dimensions</label>
                            <input type="text" name="image_dimensions" value={productData.image_dimensions} onChange={handleChange} placeholder="e.g. 24 W × 36 H in." />
                        </div>
                        <div className="form-field">
                            <label>Description</label>
                            <textarea name="product_description" value={productData.product_description} onChange={handleChange} rows={4} placeholder="Detailed description…" />
                        </div>
                        <div className="form-field">
                            <label>Short Description</label>
                            <textarea name="product_short_description" value={productData.product_short_description} onChange={handleChange} rows={4} placeholder="Brief summary…" />
                        </div>
                        <div className="form-field">
                            <label>Cost ($) <span className="req">*</span></label>
                            <input type="text" name="product_cost" value={productData.product_cost}
                                onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) handleChange(e); }}
                                placeholder="0 – 9999" />
                        </div>
                        <div className="form-field">
                            <label>Discount (%) <span className="req">*</span></label>
                            <input type="text" name="product_discount_percentage" value={productData.product_discount_percentage}
                                onChange={(e) => { if (/^\d{0,2}$/.test(e.target.value)) handleChange(e); }}
                                placeholder="0 – 99" />
                        </div>
                        <div className="form-field">
                            <label>Final Price ($)</label>
                            <input type="text" name="product_final_price" value={productData.product_final_price} disabled placeholder="Auto-calculated" />
                        </div>
                        <div className="form-field">
                            <label>Availability</label>
                            <label className="toggle-field" htmlFor="add-product-available">
                                <div className="toggle-field-label">
                                    <span>{productData.product_available ? 'Available' : 'Not Available'}</span>
                                    <small>{productData.product_available ? 'Product is visible to buyers' : 'Product is hidden from buyers'}</small>
                                </div>
                                <div className="toggle-switch">
                                    <input
                                        id="add-product-available"
                                        type="checkbox"
                                        checked={productData.product_available === 1}
                                        onChange={(e) => setProductData((p) => ({ ...p, product_available: e.target.checked ? 1 : 0 }))}
                                    />
                                    <span className="toggle-track" />
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="modal-form-grid">
                        <div className="form-field">
                            <label>Product Image <span className="req">*</span></label>
                            <input type="file" accept="image/*" onChange={handleImageChange} />
                            {productData.product_image && (
                                <div className="img-preview-wrap">
                                    <img src={productData.product_image} alt="Product preview" />
                                    <button type="button" className="img-remove-btn"
                                        onClick={() => setProductData((p) => ({ ...p, product_image: '' }))}>×</button>
                                </div>
                            )}
                        </div>
                        <div className="form-field">
                            <label>Thumbnails</label>
                            <input type="file" accept="image/*" multiple onChange={handleThumbnailChange} />
                            {isLoadingThumbnails && <span className="loading-text"><i className="fa-solid fa-spinner fa-spin"></i> Uploading…</span>}
                            {thumbnailImages.length > 0 && (
                                <div className="thumbnails-grid">
                                    {thumbnailImages.map((item, i) => (
                                        <div key={i} className="thumbnail-item">
                                            <img src={item.image} alt={`Thumb ${i + 1}`} />
                                            <button type="button" className="img-remove-btn" onClick={() => removeThumbnail(i)}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {error && <div className="modal-alert error"><i className="fa-solid fa-circle-exclamation"></i> {error}</div>}
                    {successMsg && <div className="modal-alert success"><i className="fa-solid fa-circle-check"></i> {successMsg}</div>}
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn-primary-orange" onClick={handleSubmit} disabled={!isFormValid || loading}>
                    {loading ? "Adding…" : "Add Product"}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default AddProductModal;
